import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Button,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
  Badge,
} from "reactstrap";
import { ClipLoader } from "react-spinners";
import api from "services/api";
import { useToast } from "../../../hooks/toast";
import useDebounce from "../../../hooks/useDebounce";
import "./Parametros.css";

const ALL_MODULES_KEY = "__ALL__";

const CLASSIFICACAO_OPTIONS = [
  { value: "EMBARC", label: "Embarcador", color: "info" },
  { value: "EMPRESA", label: "Empresa", color: "primary" },
  { value: "EMBARCTRANS", label: "Emb. Transportadora", color: "warning" },
  { value: "EMBARCREMET", label: "Emb. Remetente", color: "success" },
];

const TIPO_OPTIONS = [
  { value: "text", label: "Texto", color: "secondary" },
  { value: "number", label: "Numero", color: "info" },
  { value: "toggle", label: "Sim/nao", color: "primary" },
  { value: "select", label: "Lista", color: "warning" },
  { value: "time", label: "Hora", color: "success" },
  { value: "url", label: "URL", color: "default" },
  { value: "password", label: "Senha", color: "danger" },
];

function createEmptyForm() {
  return {
    Modulo: "",
    Nome: "",
    Descricao: "",
    Classificacao: "EMBARC",
    uiConfig: {
      tipo: "text",
      sufixo: "",
      min: "",
      max: "",
      placeholder: "",
      useAdvancedOptions: false,
      optionsText: "",
      optionRows: [],
    },
  };
}

function normalizeType(tipo) {
  if (tipo === "boolean") {
    return "toggle";
  }

  return tipo || "text";
}

function getNormalizedValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
}

function hasPresentValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function getClassificacaoMeta(classificacao) {
  return (
    CLASSIFICACAO_OPTIONS.find((option) => option.value === classificacao) || {
      value: classificacao,
      label: classificacao || "Nao informado",
      color: "default",
    }
  );
}

function getTipoMeta(tipo) {
  return (
    TIPO_OPTIONS.find((option) => option.value === normalizeType(tipo)) || {
      value: normalizeType(tipo),
      label: normalizeType(tipo),
      color: "secondary",
    }
  );
}

function getModuloName(item) {
  return item?.Modulo || "Geral";
}

function sortRows(items) {
  return [...items].sort((a, b) => {
    const moduleCompare = getModuloName(a).localeCompare(getModuloName(b), "pt-BR");
    if (moduleCompare !== 0) {
      return moduleCompare;
    }

    return (a?.Nome || "").localeCompare(b?.Nome || "", "pt-BR");
  });
}

function safeParseJson(rawValue) {
  if (!rawValue) {
    return [];
  }

  if (Array.isArray(rawValue)) {
    return rawValue;
  }

  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function normalizeSelectOptions(rawOptions) {
  const optionsArray = safeParseJson(rawOptions);

  return optionsArray
    .map((option) => {
      if (option && typeof option === "object") {
        const rawValue =
          option.value !== undefined && option.value !== null
            ? option.value
            : option.label;

        if (rawValue === undefined || rawValue === null || rawValue === "") {
          return null;
        }

        return {
          value: String(rawValue),
          label: option.label ? String(option.label) : String(rawValue),
        };
      }

      if (option === undefined || option === null || option === "") {
        return null;
      }

      return {
        value: String(option),
        label: String(option),
      };
    })
    .filter(Boolean);
}

function optionRowsFromOptions(rawOptions) {
  return normalizeSelectOptions(rawOptions).map((option) => ({
    value: option.value,
    label: option.label,
  }));
}

function optionRowsToPayload(optionRows) {
  return optionRows
    .map((row) => {
      const rowValue = (row?.value || row?.label || "").trim();
      const rowLabel = (row?.label || row?.value || "").trim();

      if (!rowValue && !rowLabel) {
        return null;
      }

      return {
        value: rowValue || rowLabel,
        label: rowLabel || rowValue,
      };
    })
    .filter(Boolean);
}

function optionRowsToJson(optionRows) {
  const payload = optionRowsToPayload(optionRows);

  return payload.length ? JSON.stringify(payload, null, 2) : "";
}

function hasAdvancedUi(item) {
  const tipo = normalizeType(item?.Tipo || item?.uiConfig?.tipo);
  const hasVisualConfig = Boolean(
    item?.Sufixo ||
      item?.Placeholder ||
      item?.uiConfig?.sufixo ||
      item?.uiConfig?.placeholder ||
      hasPresentValue(item?.Min) ||
      hasPresentValue(item?.Max) ||
      hasPresentValue(item?.uiConfig?.min) ||
      hasPresentValue(item?.uiConfig?.max)
  );
  const hasOptions = Boolean(item?.Opcoes || item?.uiConfig?.optionsText);

  return tipo !== "text" || hasVisualConfig || hasOptions;
}

function buildFormFromItem(item) {
  const parsedOptions = safeParseJson(item?.Opcoes);

  return {
    Modulo: item?.Modulo || "",
    Nome: item?.Nome || "",
    Descricao: item?.Descricao || "",
    Classificacao: item?.Classificacao || "EMBARC",
    uiConfig: {
      tipo: normalizeType(item?.Tipo),
      sufixo: item?.Sufixo || "",
      min: item?.Min !== null && item?.Min !== undefined ? String(item.Min) : "",
      max: item?.Max !== null && item?.Max !== undefined ? String(item.Max) : "",
      placeholder: item?.Placeholder || "",
      useAdvancedOptions: false,
      optionsText: parsedOptions.length ? JSON.stringify(parsedOptions, null, 2) : "",
      optionRows: optionRowsFromOptions(parsedOptions),
    },
  };
}

function normalizeFormForSnapshot(form) {
  return JSON.stringify({
    Modulo: (form?.Modulo || "").trim(),
    Nome: (form?.Nome || "").trim(),
    Descricao: (form?.Descricao || "").trim(),
    Classificacao: form?.Classificacao || "",
    uiConfig: {
      tipo: normalizeType(form?.uiConfig?.tipo),
      sufixo: (form?.uiConfig?.sufixo || "").trim(),
      min: getNormalizedValue(form?.uiConfig?.min),
      max: getNormalizedValue(form?.uiConfig?.max),
      placeholder: (form?.uiConfig?.placeholder || "").trim(),
      useAdvancedOptions: Boolean(form?.uiConfig?.useAdvancedOptions),
      optionsText: (form?.uiConfig?.optionsText || "").trim(),
      optionRows: optionRowsToPayload(form?.uiConfig?.optionRows || []),
    },
  });
}

function buildPreviewParam(form) {
  const uiConfig = form?.uiConfig || {};
  const tipo = normalizeType(uiConfig.tipo);
  const options = uiConfig.useAdvancedOptions
    ? normalizeSelectOptions(uiConfig.optionsText)
    : optionRowsToPayload(uiConfig.optionRows || []);

  return {
    Nome: form?.Nome || "Nome do parametro",
    Descricao: form?.Descricao || "Descreva aqui como esse parametro deve orientar a operacao.",
    Tipo: tipo,
    Opcoes: options,
    Sufixo: uiConfig.sufixo,
    Min: uiConfig.min,
    Max: uiConfig.max,
    Placeholder: uiConfig.placeholder,
  };
}

function getInitialPreviewValue(param) {
  const tipo = normalizeType(param?.Tipo);
  const options = Array.isArray(param?.Opcoes)
    ? normalizeSelectOptions(param.Opcoes)
    : normalizeSelectOptions(param?.Opcoes || []);

  if (tipo === "toggle") {
    return "Nao";
  }

  if (tipo === "select" && options.length > 0) {
    return options[0].value;
  }

  return "";
}

function renderPreviewControl({ param, value, onChange, disabled, fullWidth = false }) {
  const tipo = normalizeType(param?.Tipo);
  const options = Array.isArray(param?.Opcoes)
    ? normalizeSelectOptions(param.Opcoes)
    : normalizeSelectOptions(param?.Opcoes || []);
  const currentValue = getNormalizedValue(value);

  if (tipo === "toggle") {
    const normalizedValue = currentValue.toLowerCase();
    const isOn = normalizedValue === "sim" || normalizedValue === "true" || normalizedValue === "1";

    return (
      <button
        type="button"
        className={`catalogo-parametros-toggle ${isOn ? "catalogo-parametros-toggle--on" : ""}`}
        onClick={() => !disabled && onChange(isOn ? "Nao" : "Sim")}
        disabled={disabled}
      >
        <span className="catalogo-parametros-toggle__track">
          <span className="catalogo-parametros-toggle__thumb" />
        </span>
        <span className="catalogo-parametros-toggle__label">{isOn ? "Sim" : "Nao"}</span>
      </button>
    );
  }

  if (tipo === "select") {
    const hasCurrentOption = options.some((option) => option.value === currentValue);
    const finalOptions = hasCurrentOption || currentValue === ""
      ? options
      : [{ value: currentValue, label: currentValue }, ...options];

    return (
      <Input
        type="select"
        value={currentValue}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        style={{ width: fullWidth ? "100%" : 260 }}
      >
        <option value="">Selecione...</option>
        {finalOptions.map((option) => (
          <option key={`${param?.Nome || "preview"}-${option.value}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </Input>
    );
  }

  let inputType = "text";
  if (tipo === "number") {
    inputType = "number";
  } else if (tipo === "time") {
    inputType = "time";
  } else if (tipo === "url") {
    inputType = "url";
  } else if (tipo === "password") {
    inputType = "password";
  }

  if (inputType === "number" && currentValue !== "" && Number.isNaN(Number(currentValue))) {
    inputType = "text";
  }

  if (inputType === "time" && currentValue && !/^\d{2}:\d{2}/.test(currentValue)) {
    inputType = "text";
  }

  return (
    <div className="catalogo-parametros-control">
      <Input
        type={inputType}
        value={currentValue}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={param?.Placeholder || ""}
        min={param?.Min !== "" ? param?.Min : undefined}
        max={param?.Max !== "" ? param?.Max : undefined}
        style={{ width: fullWidth ? "100%" : 260 }}
      />
      {param?.Sufixo ? (
        <span className="catalogo-parametros-control__suffix">{param.Sufixo}</span>
      ) : null}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="catalogo-parametros-stat" style={{ "--accent": accent }}>
      <span className="catalogo-parametros-stat__label">{label}</span>
      <strong className="catalogo-parametros-stat__value">{value}</strong>
    </div>
  );
}

function OptionRowEditor({
  optionRows,
  onAddRow,
  onChangeRow,
  onRemoveRow,
}) {
  return (
    <div className="catalogo-parametros-options-editor">
      <div className="catalogo-parametros-options-editor__header">
        <span>Opcoes cadastradas</span>
        <Button color="info" size="sm" outline onClick={onAddRow}>
          Adicionar linha
        </Button>
      </div>

      {optionRows.length === 0 ? (
        <div className="catalogo-parametros-options-editor__empty">
          Nenhuma opcao adicionada. Crie ao menos uma entrada para habilitar o campo de lista.
        </div>
      ) : (
        optionRows.map((row, index) => (
          <div key={`option-row-${index}`} className="catalogo-parametros-options-editor__row">
            <Input
              type="text"
              value={row.value}
              placeholder="Valor tecnico"
              onChange={(event) => onChangeRow(index, "value", event.target.value)}
            />
            <Input
              type="text"
              value={row.label}
              placeholder="Rotulo visivel"
              onChange={(event) => onChangeRow(index, "label", event.target.value)}
            />
            <Button color="danger" outline size="sm" onClick={() => onRemoveRow(index)}>
              <i className="tim-icons icon-simple-remove" />
            </Button>
          </div>
        ))
      )}
    </div>
  );
}

function ConfigInspector({ item, previewValue, onPreviewChange, onEdit, onDelete }) {
  if (!item) {
    return (
      <Card className="catalogo-parametros-inspector-card">
        <CardBody className="text-center py-5">
          <h5 className="mb-2">Nenhum parametro selecionado</h5>
          <p className="mb-0">
            Escolha um item da tabela para inspecionar seu escopo, sua UI e seu impacto operacional.
          </p>
        </CardBody>
      </Card>
    );
  }

  const classificacaoMeta = getClassificacaoMeta(item.Classificacao);
  const tipoMeta = getTipoMeta(item.Tipo);
  const options = normalizeSelectOptions(item.Opcoes);

  return (
    <Card className="catalogo-parametros-inspector-card">
      <CardBody>
        <div className="catalogo-parametros-inspector-card__header">
          <div>
            <span className="catalogo-parametros-inspector-card__eyebrow">Detalhe do parametro</span>
            <h4 className="catalogo-parametros-inspector-card__title">{item.Nome}</h4>
          </div>
          <div className="catalogo-parametros-inspector-card__badges">
            <Badge color={tipoMeta.color} pill>
              {tipoMeta.label}
            </Badge>
            <Badge color={classificacaoMeta.color} pill>
              {classificacaoMeta.label}
            </Badge>
          </div>
        </div>

        <p className="catalogo-parametros-inspector-card__description">
          {item.Descricao || "Sem descricao funcional cadastrada."}
        </p>

        <div className="catalogo-parametros-inspector-card__meta">
          <div>
            <span>Modulo</span>
            <strong>{getModuloName(item)}</strong>
          </div>
          <div>
            <span>UI avancada</span>
            <strong>{hasAdvancedUi(item) ? "Sim" : "Basica"}</strong>
          </div>
          <div>
            <span>Opcoes</span>
            <strong>{options.length}</strong>
          </div>
        </div>

        <div className="catalogo-parametros-preview-card">
          <span className="catalogo-parametros-preview-card__label">Preview operacional</span>
          <div className="catalogo-parametros-preview-card__name">{item.Nome}</div>
          <div className="catalogo-parametros-preview-card__control">
            {renderPreviewControl({
              param: item,
              value: previewValue,
              onChange: onPreviewChange,
              disabled: false,
              fullWidth: true,
            })}
          </div>
          <div className="catalogo-parametros-preview-card__foot">
            {item.Placeholder ? <span>Placeholder: {item.Placeholder}</span> : null}
            {item.Sufixo ? <span>Sufixo: {item.Sufixo}</span> : null}
            {item.Min !== null && item.Min !== undefined ? <span>Min: {item.Min}</span> : null}
            {item.Max !== null && item.Max !== undefined ? <span>Max: {item.Max}</span> : null}
          </div>
        </div>

        <div className="catalogo-parametros-inspector-card__actions">
          <Button color="primary" onClick={() => onEdit(item)}>
            Editar
          </Button>
          <Button color="danger" outline onClick={() => onDelete(item)}>
            Excluir
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default function CatalogoParametros() {
  const { addToast } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filtroClassificacao, setFiltroClassificacao] = useState("todos");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [showAdvancedOnly, setShowAdvancedOnly] = useState(false);
  const [activeModulo, setActiveModulo] = useState(ALL_MODULES_KEY);
  const [selectedId, setSelectedId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(createEmptyForm());
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(
    normalizeFormForSnapshot(createEmptyForm())
  );
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editorPreviewValue, setEditorPreviewValue] = useState("");
  const [inspectorPreviewValue, setInspectorPreviewValue] = useState("");

  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 250);

  const notify = useCallback(
    (type, title, description) => {
      addToast({
        place: "tr",
        type,
        title,
        description,
        icon:
          type === "success"
            ? "tim-icons icon-check-2"
            : type === "danger"
            ? "tim-icons icon-alert-circle-exc"
            : "tim-icons icon-bell-55",
      });
    },
    [addToast]
  );

  const carregar = useCallback(async () => {
    setLoading(true);
    setPageError(null);

    try {
      const response = await api.get("/parametros/catalogo");
      const fetchedRows = Array.isArray(response.data) ? response.data : [];
      setRows(sortRows(fetchedRows));
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Erro ao carregar o catalogo de parametros.";

      setPageError(message);
      notify("danger", "Erro ao carregar", message);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const normalizedRows = useMemo(() => sortRows(rows), [rows]);

  const totalModulos = useMemo(
    () => Array.from(new Set(normalizedRows.map((item) => getModuloName(item)))).length,
    [normalizedRows]
  );

  const totalClassificacoes = useMemo(
    () => Array.from(new Set(normalizedRows.map((item) => item.Classificacao).filter(Boolean))).length,
    [normalizedRows]
  );

  const totalAdvanced = useMemo(
    () => normalizedRows.filter((item) => hasAdvancedUi(item)).length,
    [normalizedRows]
  );

  const moduleOptions = useMemo(
    () => Array.from(new Set(normalizedRows.map((item) => getModuloName(item)))),
    [normalizedRows]
  );

  const filteredWithoutModule = useMemo(() => {
    const normalizedSearch = debouncedSearch.trim().toLowerCase();

    return normalizedRows.filter((item) => {
      if (filtroClassificacao !== "todos" && item.Classificacao !== filtroClassificacao) {
        return false;
      }

      if (filtroTipo !== "todos" && normalizeType(item.Tipo) !== filtroTipo) {
        return false;
      }

      if (showAdvancedOnly && !hasAdvancedUi(item)) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = [
        item.Nome,
        item.Descricao,
        item.Modulo,
        getClassificacaoMeta(item.Classificacao).label,
        getTipoMeta(item.Tipo).label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [normalizedRows, filtroClassificacao, filtroTipo, showAdvancedOnly, debouncedSearch]);

  const moduleCounts = useMemo(() => {
    return moduleOptions.reduce((accumulator, modulo) => {
      accumulator[modulo] = filteredWithoutModule.filter(
        (item) => getModuloName(item) === modulo
      ).length;
      return accumulator;
    }, {});
  }, [moduleOptions, filteredWithoutModule]);

  const visibleRows = useMemo(() => {
    if (activeModulo === ALL_MODULES_KEY) {
      return filteredWithoutModule;
    }

    return filteredWithoutModule.filter((item) => getModuloName(item) === activeModulo);
  }, [filteredWithoutModule, activeModulo]);

  useEffect(() => {
    if (activeModulo !== ALL_MODULES_KEY && !moduleOptions.includes(activeModulo)) {
      setActiveModulo(ALL_MODULES_KEY);
    }
  }, [activeModulo, moduleOptions]);

  useEffect(() => {
    if (!visibleRows.length) {
      setSelectedId(null);
      return;
    }

    if (!visibleRows.some((item) => String(item.Id) === String(selectedId))) {
      setSelectedId(visibleRows[0].Id);
    }
  }, [visibleRows, selectedId]);

  const selectedItem = useMemo(
    () => visibleRows.find((item) => String(item.Id) === String(selectedId)) || null,
    [visibleRows, selectedId]
  );

  const editorPreviewParam = useMemo(() => buildPreviewParam(form), [form]);
  const editorOptions = useMemo(
    () =>
      form.uiConfig.useAdvancedOptions
        ? normalizeSelectOptions(form.uiConfig.optionsText)
        : optionRowsToPayload(form.uiConfig.optionRows),
    [form.uiConfig]
  );

  const formIsDirty = useMemo(
    () => normalizeFormForSnapshot(form) !== initialFormSnapshot,
    [form, initialFormSnapshot]
  );

  useEffect(() => {
    if (!selectedItem) {
      return;
    }

    setInspectorPreviewValue(getInitialPreviewValue(selectedItem));
  }, [selectedItem]);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    const nextInitialValue = getInitialPreviewValue(editorPreviewParam);
    const tipo = normalizeType(editorPreviewParam.Tipo);

    if (tipo === "select") {
      const hasCurrentOption = editorOptions.some(
        (option) => option.value === editorPreviewValue
      );
      if (!hasCurrentOption) {
        setEditorPreviewValue(nextInitialValue);
      }
      return;
    }

    if (tipo === "toggle" && editorPreviewValue === "") {
      setEditorPreviewValue(nextInitialValue);
    }
  }, [modalOpen, editorPreviewParam, editorOptions, editorPreviewValue]);

  useEffect(() => {
    if (!modalOpen || !formIsDirty) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [modalOpen, formIsDirty]);

  const closeEditorImmediately = useCallback(() => {
    setModalOpen(false);
    setEditItem(null);
    setForm(createEmptyForm());
    setInitialFormSnapshot(normalizeFormForSnapshot(createEmptyForm()));
    setFormError(null);
    setEditorPreviewValue("");
    setDiscardModalOpen(false);
  }, []);

  const attemptCloseEditor = useCallback(() => {
    if (saving) {
      return;
    }

    if (formIsDirty) {
      setDiscardModalOpen(true);
      return;
    }

    closeEditorImmediately();
  }, [closeEditorImmediately, formIsDirty, saving]);

  const openNew = useCallback(() => {
    const nextForm = createEmptyForm();
    setEditItem(null);
    setForm(nextForm);
    setInitialFormSnapshot(normalizeFormForSnapshot(nextForm));
    setEditorPreviewValue(getInitialPreviewValue(buildPreviewParam(nextForm)));
    setFormError(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((item) => {
    const nextForm = buildFormFromItem(item);
    setEditItem(item);
    setForm(nextForm);
    setInitialFormSnapshot(normalizeFormForSnapshot(nextForm));
    setEditorPreviewValue(getInitialPreviewValue(buildPreviewParam(nextForm)));
    setFormError(null);
    setModalOpen(true);
  }, []);

  const updateForm = useCallback((updater) => {
    setForm((currentForm) => {
      const nextForm = typeof updater === "function" ? updater(currentForm) : updater;
      return nextForm;
    });
  }, []);

  const updateUiConfig = useCallback(
    (field, value) => {
      updateForm((currentForm) => ({
        ...currentForm,
        uiConfig: {
          ...currentForm.uiConfig,
          [field]: value,
        },
      }));
    },
    [updateForm]
  );

  const handleOptionRowChange = useCallback(
    (index, field, value) => {
      updateForm((currentForm) => ({
        ...currentForm,
        uiConfig: {
          ...currentForm.uiConfig,
          optionRows: currentForm.uiConfig.optionRows.map((row, rowIndex) =>
            rowIndex === index ? { ...row, [field]: value } : row
          ),
        },
      }));
    },
    [updateForm]
  );

  const handleAddOptionRow = useCallback(() => {
    updateForm((currentForm) => ({
      ...currentForm,
      uiConfig: {
        ...currentForm.uiConfig,
        optionRows: [...currentForm.uiConfig.optionRows, { value: "", label: "" }],
      },
    }));
  }, [updateForm]);

  const handleRemoveOptionRow = useCallback(
    (index) => {
      updateForm((currentForm) => ({
        ...currentForm,
        uiConfig: {
          ...currentForm.uiConfig,
          optionRows: currentForm.uiConfig.optionRows.filter((_, rowIndex) => rowIndex !== index),
        },
      }));
    },
    [updateForm]
  );

  const toggleAdvancedOptionsMode = useCallback(
    (useAdvancedOptions) => {
      if (useAdvancedOptions) {
        updateForm((currentForm) => ({
          ...currentForm,
          uiConfig: {
            ...currentForm.uiConfig,
            useAdvancedOptions: true,
            optionsText: currentForm.uiConfig.useAdvancedOptions
              ? currentForm.uiConfig.optionsText
              : optionRowsToJson(currentForm.uiConfig.optionRows),
          },
        }));
        return;
      }

      const parsedRows = optionRowsFromOptions(form.uiConfig.optionsText);

      if (form.uiConfig.optionsText.trim() && !parsedRows.length) {
        setFormError("JSON de opcoes invalido. Corrija antes de voltar ao editor visual.");
        return;
      }

      updateForm((currentForm) => ({
        ...currentForm,
        uiConfig: {
          ...currentForm.uiConfig,
          useAdvancedOptions: false,
          optionRows: parsedRows,
        },
      }));
    },
    [form.uiConfig.optionsText, updateForm]
  );

  const validateForm = useCallback(() => {
    const modulo = (form.Modulo || "").trim();
    const nome = (form.Nome || "").trim();
    const classificacao = form.Classificacao;
    const tipo = normalizeType(form.uiConfig.tipo);
    const placeholder = (form.uiConfig.placeholder || "").trim();
    const sufixo = (form.uiConfig.sufixo || "").trim();

    if (!modulo || !nome || !classificacao) {
      return { error: "Modulo, nome e classificacao sao obrigatorios." };
    }

    if (tipo === "number") {
      const min = form.uiConfig.min;
      const max = form.uiConfig.max;

      if (min !== "" && Number.isNaN(Number(min))) {
        return { error: "O minimo deve ser um numero valido." };
      }

      if (max !== "" && Number.isNaN(Number(max))) {
        return { error: "O maximo deve ser um numero valido." };
      }

      if (min !== "" && max !== "" && Number(min) > Number(max)) {
        return { error: "O minimo nao pode ser maior que o maximo." };
      }
    }

    let parsedOptions = null;
    if (tipo === "select") {
      if (form.uiConfig.useAdvancedOptions) {
        if (!form.uiConfig.optionsText.trim()) {
          return { error: "Cadastre ao menos uma opcao para o campo de lista." };
        }

        parsedOptions = normalizeSelectOptions(form.uiConfig.optionsText);
        if (!parsedOptions.length) {
          return { error: "JSON de opcoes invalido ou vazio." };
        }
      } else {
        parsedOptions = optionRowsToPayload(form.uiConfig.optionRows);
        if (!parsedOptions.length) {
          return { error: "Cadastre ao menos uma opcao no editor visual." };
        }
      }

      const duplicatedValues = parsedOptions.reduce((accumulator, option) => {
        const normalizedValue = String(option.value).trim().toLowerCase();
        accumulator[normalizedValue] = (accumulator[normalizedValue] || 0) + 1;
        return accumulator;
      }, {});

      if (Object.values(duplicatedValues).some((count) => count > 1)) {
        return { error: "Nao use valores tecnicos duplicados nas opcoes." };
      }
    }

    return {
      payload: {
        Modulo: modulo,
        Nome: nome,
        Descricao: (form.Descricao || "").trim(),
        Classificacao: classificacao,
        uiConfig: {
          tipo,
          opcoes: parsedOptions,
          sufixo: sufixo || null,
          min: form.uiConfig.min !== "" ? Number(form.uiConfig.min) : null,
          max: form.uiConfig.max !== "" ? Number(form.uiConfig.max) : null,
          placeholder: placeholder || null,
        },
      },
    };
  }, [form]);

  const handleSave = useCallback(async () => {
    const validation = validateForm();

    if (!validation.payload) {
      setFormError(validation.error);
      notify("danger", "Validacao", validation.error);
      return;
    }

    setSaving(true);
    setFormError(null);

    try {
      if (editItem) {
        await api.put(`/parametros/catalogo/${editItem.Id}`, validation.payload);
      } else {
        await api.post("/parametros/catalogo", validation.payload);
      }

      await carregar();
      notify(
        "success",
        editItem ? "Parametro atualizado" : "Parametro criado",
        editItem
          ? `${validation.payload.Nome} foi atualizado no catalogo.`
          : `${validation.payload.Nome} foi criado no catalogo.`
      );
      closeEditorImmediately();
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Erro ao salvar o parametro.";

      setFormError(message);
      notify("danger", "Erro ao salvar", message);
    } finally {
      setSaving(false);
    }
  }, [carregar, closeEditorImmediately, editItem, notify, validateForm]);

  const handleDelete = useCallback((item) => {
    setDeleteItem(item);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteItem) {
      return;
    }

    setDeleting(true);

    try {
      await api.delete(`/parametros/catalogo/${deleteItem.Id}`);
      await carregar();

      if (String(selectedId) === String(deleteItem.Id)) {
        setSelectedId(null);
      }

      notify(
        "success",
        "Parametro excluido",
        `${deleteItem.Nome} foi removido do catalogo e seus vinculos deixam de existir.`
      );
      setDeleteItem(null);
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Erro ao excluir o parametro.";

      notify("danger", "Erro ao excluir", message);
    } finally {
      setDeleting(false);
    }
  }, [carregar, deleteItem, notify, selectedId]);

  return (
    <div className="content">
      <Row>
        <Col md={12}>
          <Card className="catalogo-parametros-hero">
            <CardBody>
              <div className="catalogo-parametros-hero__top">
                <div className="catalogo-parametros-hero__intro">
                  <span className="catalogo-parametros-hero__eyebrow">Governanca de metadados</span>
                  <h3 className="catalogo-parametros-hero__title">Catalogo de parametros</h3>
                  <p className="catalogo-parametros-hero__description">
                    Defina como a plataforma expoe configuracoes para embarcador, empresa,
                    transportadora e remetente com mais consistencia operacional.
                  </p>
                  <div className="catalogo-parametros-hero__context">
                    <Badge color="info" pill>
                      Admin
                    </Badge>
                    <span>Fonte unica para regras de configuracao em todo o produto.</span>
                    {loading ? <ClipLoader size={16} color="#1d8cf8" /> : null}
                  </div>
                  {pageError ? (
                    <div className="catalogo-parametros-inline-error">{pageError}</div>
                  ) : null}
                </div>

                <div className="catalogo-parametros-hero__actions">
                  <div className="catalogo-parametros-hero__callout">
                    <span className="catalogo-parametros-hero__callout-label">Risco operacional</span>
                    <strong>Alteracoes no catalogo impactam telas e valores vinculados</strong>
                    <small>
                      Use descricoes claras, escopos corretos e tipagem consistente antes de salvar.
                    </small>
                  </div>

                  <div className="catalogo-parametros-hero__buttons">
                    <Button color="default" outline onClick={carregar} disabled={loading}>
                      Atualizar
                    </Button>
                    <Button color="primary" onClick={openNew}>
                      Novo parametro
                    </Button>
                  </div>
                </div>
              </div>

              <Row className="mt-4">
                <Col md={6} lg={3}>
                  <StatCard label="Itens no catalogo" value={normalizedRows.length} accent="#1d8cf8" />
                </Col>
                <Col md={6} lg={3}>
                  <StatCard label="Modulos ativos" value={totalModulos} accent="#00bf9a" />
                </Col>
                <Col md={6} lg={3}>
                  <StatCard label="Classificacoes" value={totalClassificacoes} accent="#ffb547" />
                </Col>
                <Col md={6} lg={3}>
                  <StatCard label="UI avancada" value={totalAdvanced} accent="#fd5d93" />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="catalogo-parametros-toolbar">
            <CardBody>
              <Row>
                <Col lg={5}>
                  <FormGroup>
                    <Label className="catalogo-parametros-label">Buscar no catalogo</Label>
                    <Input
                      type="text"
                      value={searchTerm}
                      placeholder="Nome, descricao, modulo, escopo ou tipo"
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                  </FormGroup>
                </Col>

                <Col md={4} lg={2}>
                  <FormGroup>
                    <Label className="catalogo-parametros-label">Classificacao</Label>
                    <Input
                      type="select"
                      value={filtroClassificacao}
                      onChange={(event) => setFiltroClassificacao(event.target.value)}
                    >
                      <option value="todos">Todas</option>
                      {CLASSIFICACAO_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>

                <Col md={4} lg={2}>
                  <FormGroup>
                    <Label className="catalogo-parametros-label">Tipo de UI</Label>
                    <Input
                      type="select"
                      value={filtroTipo}
                      onChange={(event) => setFiltroTipo(event.target.value)}
                    >
                      <option value="todos">Todos</option>
                      {TIPO_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>

                <Col md={4} lg={3}>
                  <FormGroup className="catalogo-parametros-toolbar__summary">
                    <Label className="catalogo-parametros-label">Visibilidade</Label>
                    <div className="catalogo-parametros-toolbar__summary-row">
                      <Button
                        color={showAdvancedOnly ? "info" : "default"}
                        outline={!showAdvancedOnly}
                        onClick={() => setShowAdvancedOnly((currentValue) => !currentValue)}
                      >
                        {showAdvancedOnly ? "Somente avancados" : "Todos os itens"}
                      </Button>
                      <Button
                        color="default"
                        outline
                        onClick={() => {
                          setSearchTerm("");
                          setFiltroClassificacao("todos");
                          setFiltroTipo("todos");
                          setShowAdvancedOnly(false);
                          setActiveModulo(ALL_MODULES_KEY);
                        }}
                      >
                        Limpar
                      </Button>
                    </div>
                    <span>{visibleRows.length} parametro(s) no recorte atual</span>
                  </FormGroup>
                </Col>
              </Row>

              <div className="catalogo-parametros-module-pills">
                <button
                  type="button"
                  className={`catalogo-parametros-module-pill ${
                    activeModulo === ALL_MODULES_KEY ? "catalogo-parametros-module-pill--active" : ""
                  }`}
                  onClick={() => setActiveModulo(ALL_MODULES_KEY)}
                >
                  <span>Todos</span>
                  <strong>{filteredWithoutModule.length}</strong>
                </button>

                {moduleOptions.map((modulo) => (
                  <button
                    key={modulo}
                    type="button"
                    className={`catalogo-parametros-module-pill ${
                      activeModulo === modulo ? "catalogo-parametros-module-pill--active" : ""
                    } ${moduleCounts[modulo] === 0 ? "catalogo-parametros-module-pill--muted" : ""}`}
                    onClick={() => setActiveModulo(modulo)}
                  >
                    <span>{modulo}</span>
                    <strong>{moduleCounts[modulo] || 0}</strong>
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={8}>
          <Card className="catalogo-parametros-table-card">
            <CardHeader className="catalogo-parametros-table-card__header">
              <div>
                <CardTitle tag="h5" className="mb-1">
                  Itens do catalogo
                </CardTitle>
                <small>
                  {activeModulo === ALL_MODULES_KEY
                    ? "Visao geral por modulo"
                    : `Recorte focado em ${activeModulo}`}
                </small>
              </div>
              <Badge color="default" pill>
                {visibleRows.length}
              </Badge>
            </CardHeader>

            <CardBody className="catalogo-parametros-table-card__body">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : visibleRows.length === 0 ? (
                <div className="catalogo-parametros-empty-state">
                  <h5 className="mb-2">Nenhum parametro encontrado</h5>
                  <p className="mb-0">
                    Ajuste os filtros ou limpe o recorte atual para voltar a enxergar o catalogo.
                  </p>
                </div>
              ) : (
                <Table responsive hover className="catalogo-parametros-table">
                  <thead>
                    <tr>
                      <th>Parametro</th>
                      <th>Modulo</th>
                      <th>Escopo</th>
                      <th>UI</th>
                      <th className="text-right">Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((item) => {
                      const classificacaoMeta = getClassificacaoMeta(item.Classificacao);
                      const tipoMeta = getTipoMeta(item.Tipo);
                      const isSelected = String(item.Id) === String(selectedId);

                      return (
                        <tr
                          key={item.Id}
                          className={isSelected ? "catalogo-parametros-table__row--selected" : ""}
                          onClick={() => setSelectedId(item.Id)}
                        >
                          <td>
                            <div className="catalogo-parametros-table__name">{item.Nome}</div>
                            <div className="catalogo-parametros-table__description">
                              {item.Descricao || "Sem descricao funcional."}
                            </div>
                          </td>
                          <td>
                            <Badge color="default" pill className="catalogo-parametros-table__module">
                              {getModuloName(item)}
                            </Badge>
                          </td>
                          <td>
                            <Badge color={classificacaoMeta.color} pill>
                              {classificacaoMeta.label}
                            </Badge>
                          </td>
                          <td>
                            <div className="catalogo-parametros-table__ui">
                              <Badge color={tipoMeta.color} pill>
                                {tipoMeta.label}
                              </Badge>
                              {hasAdvancedUi(item) ? (
                                <Badge color="warning" pill>
                                  Avancado
                                </Badge>
                              ) : null}
                            </div>
                          </td>
                          <td className="text-right">
                            <Button
                              color="primary"
                              size="sm"
                              outline
                              className="mr-1"
                              onClick={(event) => {
                                event.stopPropagation();
                                openEdit(item);
                              }}
                            >
                              Editar
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDelete(item);
                              }}
                            >
                              Excluir
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col xl={4}>
          <ConfigInspector
            item={selectedItem}
            previewValue={inspectorPreviewValue}
            onPreviewChange={setInspectorPreviewValue}
            onEdit={openEdit}
            onDelete={handleDelete}
          />
        </Col>
      </Row>

      <Modal
        isOpen={modalOpen}
        toggle={attemptCloseEditor}
        size="lg"
        fade={false}
        modalClassName="modal-align-top catalogo-parametros-modal"
      >
        <div className="catalogo-parametros-modal__header">
          <div>
            <div className="catalogo-parametros-modal__eyebrow">
              {editItem ? "Edicao de parametro" : "Novo parametro"}
            </div>
            <h4 className="catalogo-parametros-modal__title">
              {editItem ? editItem.Nome : "Criar item do catalogo"}
            </h4>
          </div>

          <div className="catalogo-parametros-modal__header-right">
            {formIsDirty ? (
              <Badge color="warning" pill>
                Alteracoes nao salvas
              </Badge>
            ) : (
              <Badge color="default" pill>
                Sincronizado
              </Badge>
            )}
            <button
              type="button"
              className="catalogo-parametros-modal__close"
              onClick={attemptCloseEditor}
            >
              x
            </button>
          </div>
        </div>

        <ModalBody>
          {formError ? <div className="catalogo-parametros-inline-error">{formError}</div> : null}

          <Row>
            <Col lg={7}>
              <Card className="catalogo-parametros-section-card">
                <CardBody>
                  <div className="catalogo-parametros-section-card__title">1. Identidade e escopo</div>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Modulo *</Label>
                        <Input
                          type="text"
                          value={form.Modulo}
                          placeholder="Ex: FINANCEIRO"
                          onChange={(event) => updateForm({
                            ...form,
                            Modulo: event.target.value,
                          })}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Classificacao *</Label>
                        <Input
                          type="select"
                          value={form.Classificacao}
                          onChange={(event) => updateForm({
                            ...form,
                            Classificacao: event.target.value,
                          })}
                        >
                          {CLASSIFICACAO_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>Nome *</Label>
                    <Input
                      type="text"
                      value={form.Nome}
                      placeholder="Nome do parametro"
                      onChange={(event) => updateForm({
                        ...form,
                        Nome: event.target.value,
                      })}
                    />
                  </FormGroup>

                  <FormGroup className="mb-0">
                    <Label>Descricao funcional</Label>
                    <Input
                      type="textarea"
                      rows={3}
                      value={form.Descricao}
                      placeholder="Explique quando esse parametro deve ser usado e qual impacto ele produz."
                      onChange={(event) => updateForm({
                        ...form,
                        Descricao: event.target.value,
                      })}
                    />
                  </FormGroup>
                </CardBody>
              </Card>

              <Card className="catalogo-parametros-section-card">
                <CardBody>
                  <div className="catalogo-parametros-section-card__title">2. Tipo e comportamento</div>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Tipo de controle</Label>
                        <Input
                          type="select"
                          value={form.uiConfig.tipo}
                          onChange={(event) =>
                            updateUiConfig("tipo", event.target.value)
                          }
                        >
                          {TIPO_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>Placeholder</Label>
                        <Input
                          type="text"
                          value={form.uiConfig.placeholder}
                          placeholder="Texto auxiliar"
                          onChange={(event) =>
                            updateUiConfig("placeholder", event.target.value)
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Sufixo</Label>
                        <Input
                          type="text"
                          value={form.uiConfig.sufixo}
                          placeholder="Ex: kg, dias, %"
                          onChange={(event) =>
                            updateUiConfig("sufixo", event.target.value)
                          }
                        />
                      </FormGroup>
                    </Col>

                    <Col md={3}>
                      <FormGroup>
                        <Label>Minimo</Label>
                        <Input
                          type="number"
                          value={form.uiConfig.min}
                          disabled={normalizeType(form.uiConfig.tipo) !== "number"}
                          onChange={(event) => updateUiConfig("min", event.target.value)}
                        />
                      </FormGroup>
                    </Col>

                    <Col md={3}>
                      <FormGroup>
                        <Label>Maximo</Label>
                        <Input
                          type="number"
                          value={form.uiConfig.max}
                          disabled={normalizeType(form.uiConfig.tipo) !== "number"}
                          onChange={(event) => updateUiConfig("max", event.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {normalizeType(form.uiConfig.tipo) === "select" ? (
                <Card className="catalogo-parametros-section-card">
                  <CardBody>
                    <div className="catalogo-parametros-section-card__title">
                      3. Configuracao das opcoes
                    </div>

                    <div className="catalogo-parametros-mode-switch">
                      <Button
                        color={form.uiConfig.useAdvancedOptions ? "default" : "info"}
                        outline={form.uiConfig.useAdvancedOptions}
                        size="sm"
                        onClick={() => toggleAdvancedOptionsMode(false)}
                      >
                        Editor visual
                      </Button>
                      <Button
                        color={form.uiConfig.useAdvancedOptions ? "info" : "default"}
                        outline={!form.uiConfig.useAdvancedOptions}
                        size="sm"
                        onClick={() => toggleAdvancedOptionsMode(true)}
                      >
                        JSON avancado
                      </Button>
                    </div>

                    {form.uiConfig.useAdvancedOptions ? (
                      <FormGroup className="mb-0">
                        <Label>JSON de opcoes</Label>
                        <Input
                          type="textarea"
                          rows={8}
                          value={form.uiConfig.optionsText}
                          placeholder='[{"value":"sim","label":"Sim"}]'
                          onChange={(event) =>
                            updateUiConfig("optionsText", event.target.value)
                          }
                        />
                        <small className="catalogo-parametros-helper">
                          Use uma lista JSON. Cada item pode ser string simples ou objeto com
                          `value` e `label`.
                        </small>
                      </FormGroup>
                    ) : (
                      <OptionRowEditor
                        optionRows={form.uiConfig.optionRows}
                        onAddRow={handleAddOptionRow}
                        onChangeRow={handleOptionRowChange}
                        onRemoveRow={handleRemoveOptionRow}
                      />
                    )}
                  </CardBody>
                </Card>
              ) : null}
            </Col>

            <Col lg={5}>
              <Card className="catalogo-parametros-section-card catalogo-parametros-section-card--sticky">
                <CardBody>
                  <div className="catalogo-parametros-section-card__title">4. Preview final</div>

                  <div className="catalogo-parametros-preview">
                    <span className="catalogo-parametros-preview__label">Como o usuario vera</span>
                    <div className="catalogo-parametros-preview__name">
                      {editorPreviewParam.Nome}
                    </div>
                    <div className="catalogo-parametros-preview__description">
                      {editorPreviewParam.Descricao}
                    </div>
                    <div className="catalogo-parametros-preview__control">
                      {renderPreviewControl({
                        param: editorPreviewParam,
                        value: editorPreviewValue,
                        onChange: setEditorPreviewValue,
                        disabled: false,
                        fullWidth: true,
                      })}
                    </div>
                  </div>

                  <div className="catalogo-parametros-governance">
                    <div className="catalogo-parametros-section-card__title">5. Checklist operacional</div>
                    <div className="catalogo-parametros-governance__item">
                      <span>Escopo</span>
                      <strong>{getClassificacaoMeta(form.Classificacao).label}</strong>
                    </div>
                    <div className="catalogo-parametros-governance__item">
                      <span>Tipo</span>
                      <strong>{getTipoMeta(form.uiConfig.tipo).label}</strong>
                    </div>
                    <div className="catalogo-parametros-governance__item">
                      <span>Opcoes configuradas</span>
                      <strong>{editorOptions.length}</strong>
                    </div>
                    <div className="catalogo-parametros-governance__item">
                      <span>UI avancada</span>
                      <strong>{hasAdvancedUi({ uiConfig: form.uiConfig }) ? "Sim" : "Basica"}</strong>
                    </div>
                    <div className="catalogo-parametros-governance__warning">
                      Excluir um item do catalogo remove tambem os valores vinculados em todos os contextos.
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button color="default" outline onClick={attemptCloseEditor} disabled={saving}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : editItem ? "Salvar parametro" : "Criar parametro"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={discardModalOpen}
        toggle={() => setDiscardModalOpen(false)}
        fade={false}
        modalClassName="modal-align-top"
      >
        <div className="catalogo-parametros-modal__header">
          <div>
            <div className="catalogo-parametros-modal__eyebrow">Alteracoes pendentes</div>
            <h4 className="catalogo-parametros-modal__title">Descartar edicao?</h4>
          </div>
          <button
            type="button"
            className="catalogo-parametros-modal__close"
            onClick={() => setDiscardModalOpen(false)}
          >
            x
          </button>
        </div>

        <ModalBody>
          <p className="catalogo-parametros-modal__copy">
            Existem alteracoes nao salvas no formulario atual. Se voce sair agora, todo o trabalho
            local sera perdido.
          </p>
        </ModalBody>

        <ModalFooter>
          <Button color="default" outline onClick={() => setDiscardModalOpen(false)}>
            Continuar editando
          </Button>
          <Button color="danger" onClick={closeEditorImmediately}>
            Descartar
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={Boolean(deleteItem)}
        toggle={() => setDeleteItem(null)}
        fade={false}
        modalClassName="modal-align-top"
      >
        <div className="catalogo-parametros-modal__header">
          <div>
            <div className="catalogo-parametros-modal__eyebrow">Exclusao de alto impacto</div>
            <h4 className="catalogo-parametros-modal__title">
              {deleteItem ? deleteItem.Nome : "Excluir parametro"}
            </h4>
          </div>
          <button
            type="button"
            className="catalogo-parametros-modal__close"
            onClick={() => setDeleteItem(null)}
          >
            x
          </button>
        </div>

        <ModalBody>
          {deleteItem ? (
            <div className="catalogo-parametros-delete">
              <div className="catalogo-parametros-delete__badges">
                <Badge color="default" pill>
                  {getModuloName(deleteItem)}
                </Badge>
                <Badge color={getClassificacaoMeta(deleteItem.Classificacao).color} pill>
                  {getClassificacaoMeta(deleteItem.Classificacao).label}
                </Badge>
                <Badge color={getTipoMeta(deleteItem.Tipo).color} pill>
                  {getTipoMeta(deleteItem.Tipo).label}
                </Badge>
              </div>

              <p>
                Essa exclusao remove o parametro do catalogo e invalida todos os valores ja
                vinculados a ele em embarcadores, empresas ou outros contextos.
              </p>
              <p className="mb-0">
                O backend atual nao informa contagem de vinculos, portanto trate esta acao como
                irreversivel do ponto de vista operacional.
              </p>
            </div>
          ) : null}
        </ModalBody>

        <ModalFooter>
          <Button color="default" outline onClick={() => setDeleteItem(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button color="danger" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" /> : "Excluir parametro"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
